import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Clock, XCircle, MessageCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL || '';
const DEFAULT_PHONE = '0694274051';
const MAX_POLLS_OK = 12; // réponses 200 non appliquées (≈ 30 s)
const MAX_POLLS_ERR = 8; // erreurs réseau / 5xx (≈ 20 s)

function whatsappUrl(phone) {
  const digits = String(phone || DEFAULT_PHONE).replace(/\D/g, '').replace(/^(?:00)?594/, '').replace(/^0/, '');
  return `https://wa.me/594${digits}`;
}

/** Page publique après paiement acompte SumUp (devis CADBOT). */
export default function AcompteDevisSuccesPage() {
  const { t, formatCurrency } = useLanguage();
  const [params] = useSearchParams();
  const ref = params.get('ref') || '';
  const checkoutId = params.get('checkout_id') || params.get('id') || '';
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const [phone, setPhone] = useState(DEFAULT_PHONE);

  useEffect(() => {
    axios.get(`${API}/api/public/site-settings`)
      .then((r) => setPhone(r.data?.whatsapp || r.data?.telephone || DEFAULT_PHONE))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!ref && !checkoutId) {
      setError(t('acompteSuccess.errorIncompleteLink'));
      return undefined;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const qs = checkoutId
          ? `checkout_id=${encodeURIComponent(checkoutId)}`
          : `ref=${encodeURIComponent(ref)}`;
        const res = await axios.get(`${API}/api/payments/sumup/chat-devis/complete?${qs}`);
        if (cancelled) return;
        setStatus(res.data);
        const done = res.data?.applied || res.data?.needs_staff_confirmation;
        if (done) return;
        if (attempts < MAX_POLLS_OK) {
          setTimeout(() => setAttempts((a) => a + 1), 2500);
        } else {
          setTimedOut(true);
        }
      } catch (err) {
        if (cancelled) return;
        if (attempts < MAX_POLLS_ERR) {
          setTimeout(() => setAttempts((a) => a + 1), 2500);
        } else {
          setError(err.response?.data?.detail || t('acompteSuccess.errorCannotConfirm'));
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [ref, checkoutId, attempts]);

  const applied = status?.applied || status?.status === 'PAID_APPLIED';
  const staffPending = !applied && !!status?.needs_staff_confirmation;
  const sumupFailed = !applied && ['FAILED', 'CANCELLED', 'EXPIRED'].includes(String(status?.status || '').toUpperCase());
  const pending = status && !applied && !staffPending && !sumupFailed && !timedOut;
  const wa = whatsappUrl(phone);
  const refLabel = status?.ref_commande || status?.checkout_id || ref || checkoutId;

  const WhatsAppFallback = ({ label }) => (
    <a
      href={`${wa}?text=${encodeURIComponent(t('acompteSuccess.whatsappMessage', { ref: refLabel }))}`}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 mt-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold"
      data-testid="acompte-whatsapp-fallback"
    >
      <MessageCircle className="w-4 h-4" />
      {label || t('acompteSuccess.whatsappDefault')}
    </a>
  );

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4" data-testid="acompte-devis-succes">
      <div className="max-w-md w-full rounded-2xl border border-white/10 bg-white/5 p-8 text-center space-y-4">
        <h1 className="text-xl font-bold text-white">R.E.Mobiles</h1>
        {error ? (
          <>
            <XCircle className="w-16 h-16 text-red-400 mx-auto" />
            <p className="text-red-300 text-sm">{error}</p>
            <p className="text-gray-400 text-sm">{t('acompteSuccess.errorHint')}</p>
            <WhatsAppFallback />
          </>
        ) : applied ? (
          <>
            <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto" />
            <h2 className="text-2xl font-bold text-white">{t('acompteSuccess.appliedTitle')}</h2>
            <p className="text-gray-300 text-sm">
              {t('acompteSuccess.orderRegisteredLead')}
              {status?.ref_commande ? (
                <> <strong className="text-cyan-300">{status.ref_commande}</strong></>
              ) : null}
              {' '}{t('acompteSuccess.orderRegisteredTail')}
            </p>
            {status?.amount != null && (
              <p className="text-emerald-300 font-bold">{formatCurrency(Number(status.amount))}</p>
            )}
          </>
        ) : staffPending ? (
          <>
            <CheckCircle className="w-16 h-16 text-amber-400 mx-auto" />
            <h2 className="text-xl font-bold text-white">{t('acompteSuccess.staffPendingTitle')}</h2>
            <p className="text-gray-300 text-sm">{t('acompteSuccess.staffPendingBody')}</p>
            <WhatsAppFallback label={t('acompteSuccess.whatsappQuestion')} />
          </>
        ) : sumupFailed ? (
          <>
            <XCircle className="w-16 h-16 text-red-400 mx-auto" />
            <h2 className="text-xl font-bold text-white">{t('acompteSuccess.sumupFailedTitle')}</h2>
            <p className="text-gray-300 text-sm">{t('acompteSuccess.sumupFailedBody', { status: status?.status })}</p>
            <WhatsAppFallback />
          </>
        ) : timedOut ? (
          <>
            <Clock className="w-16 h-16 text-amber-400 mx-auto" />
            <h2 className="text-xl font-bold text-white">{t('acompteSuccess.timedOutTitle')}</h2>
            <p className="text-gray-300 text-sm">{t('acompteSuccess.timedOutBody')}</p>
            <button
              type="button"
              onClick={() => { setTimedOut(false); setAttempts(0); }}
              className="text-cyan-400 hover:text-cyan-300 text-sm font-semibold underline"
            >
              {t('acompteSuccess.recheckNow')}
            </button>
            <div>
              <WhatsAppFallback label={t('acompteSuccess.whatsappProof')} />
            </div>
          </>
        ) : (
          <>
            <Clock className={`w-16 h-16 text-amber-400 mx-auto ${pending ? 'animate-pulse' : ''}`} />
            <h2 className="text-xl font-bold text-white">{t('acompteSuccess.verifyingTitle')}</h2>
            <p className="text-gray-400 text-sm">{t('acompteSuccess.verifyingBody')}</p>
          </>
        )}
        <Link to="/" className="inline-block mt-4 text-cyan-400 hover:text-cyan-300 text-sm font-semibold">
          {t('acompteSuccess.backHome')}
        </Link>
      </div>
    </div>
  );
}
